import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { mergeMap, tap } from 'rxjs/internal/operators';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AuthService, GoogleAnalyticsService, UserService } from '../..';
import { PostDeviceNames, PostHideDevicesLed, PostShowDevicesLed } from '../../../afb-api/actions/device.actions';
import {
  GetOrganizationUsers,
  PostGroupInvitation,
  PostOrganizationInvitation,
} from '../../../afb-api/actions/organization-user.actions';
import { OrganizationUserRole } from '../../../afb-api/enums/organization-user-role.enum';
import { DeviceNameSetting, DeviceWithUuid } from '../../../afb-api/models/device';
import { OrganizationUser, SelfOrganizationUser } from '../../../afb-api/models/organization-user';
import * as fromAfbApi from '../../../afb-api/reducers';
import { Logout } from '../../../afb-graphql-api/actions/auth.actions';
import { User } from '../../../afb-graphql-api/models/auth';
import * as fromAfbGraphqlApi from '../../../afb-graphql-api/reducers';
import { GroupComponent } from '../../../dashboard/group/group.component';
import { ClipboardService } from '../../../dashboard/shared';
import {
  OrganizationListDialogContainer,
} from '../../../organization/containers/organization-list-dialog/organization-list-dialog.container';
import * as fromRoot from '../../../reducers';
import { TemperatureUnit } from '../../../shared';
import { ClearState } from '../../actions/core.actions';
import { AccessMgmtDialogComponent } from '../../components/access-mgmt-dialog/access-mgmt-dialog.component';
import {
  DeviceNameEditorDialogComponent,
} from '../../components/device-name-editor-dialog/device-name-editor-dialog.component';
import { DeviceSettingDialogComponent } from '../../components/device-setting-dialog/device-setting-dialog.component';
import { P13nDialogComponent } from '../../components/p13n-dialog/p13n-dialog.component';
import { OrganizationWithToken } from '../../models/core';
import * as coreUser from '../../store/actions/user.action';

// Not using barrels to prevent circular dependencies
@Component({
  selector: 'awair-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  rootRoute: string = '';

  developerToken: string = '';

  isInitialized: boolean = false;

  accessMgmtDialog: MatDialogRef<AccessMgmtDialogComponent>;
  deviceSettingsDialog: MatDialogRef<DeviceSettingDialogComponent>;
  deviceNameEditorDialog: MatDialogRef<DeviceNameEditorDialogComponent>;
  OrganizationUserRole = OrganizationUserRole;

  accessToken$: Observable<string> = this.afbGraphqlApiStore.pipe(
    select(fromAfbGraphqlApi.accessToken)
  );
  isLoggedIn$: Observable<boolean> = this.afbGraphqlApiStore.pipe(
    select(fromAfbGraphqlApi.isLoginLoaded)
  );
  user$: Observable<User> = this.afbGraphqlApiStore.pipe(
    select(fromAfbGraphqlApi.user)
  );
  selectedOrganization$: Observable<
    OrganizationWithToken
  > = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter(
      (selectedOrganization: OrganizationWithToken) =>
        selectedOrganization != null
    )
  );
  selfOrganizationUser$: Observable<
    SelfOrganizationUser
  > = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser),
    filter(
      (selfOrganizationUser: SelfOrganizationUser) =>
        selfOrganizationUser != null
    )
  );
  devices$: Observable<DeviceWithUuid[]> = this.afbApiStore.pipe(
    select(fromAfbApi.devicesWithUuid),
    filter((devices: DeviceWithUuid[]) => devices != null)
  );

  isPostDeviceNamesLoading$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostDeviceNamesLoading)
  );
  isPostDeviceNamesLoaded$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostDeviceNamesLoaded),
    filter((isPostDeviceNamesLoaded: boolean) => isPostDeviceNamesLoaded)
  );
  postDeviceNamesError$: Observable<string> = this.afbApiStore.pipe(
    select(fromAfbApi.postDeviceNamesError),
    filter((postDeviceNamesError: string) => postDeviceNamesError != null)
  );

  isPostShowDevicesLedLoading$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostShowDevicesLedLoading)
  );
  isPostShowDevicesLedLoaded$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostShowDevicesLedLoaded),
    filter((isPostShowDevicesLedLoaded: boolean) => isPostShowDevicesLedLoaded)
  );
  postShowDevicesLedError$: Observable<string> = this.afbApiStore.pipe(
    select(fromAfbApi.postShowDevicesLedError),
    filter((postShowDevicesLedError: string) => postShowDevicesLedError != null)
  );

  isPostHideDevicesLedLoading$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostHideDevicesLedLoading)
  );
  isPostHideDevicesLedLoaded$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isPostHideDevicesLedLoaded),
    filter((isPostHideDevicesLedLoaded: boolean) => isPostHideDevicesLedLoaded)
  );
  postHideDevicesLedError$: Observable<string> = this.afbApiStore.pipe(
    select(fromAfbApi.postHideDevicesLedError),
    filter((postHideDevicesLedError: string) => postHideDevicesLedError != null)
  );

  private destroyed$ = new Subject<boolean>();

  constructor(
    private rootStore: Store<fromRoot.State>,
    private afbApiStore: Store<fromAfbApi.State>,
    private afbGraphqlApiStore: Store<fromAfbGraphqlApi.State>,
    private actions$: Actions,
    private authService: AuthService,
    private userService: UserService,
    private clipboardService: ClipboardService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    /**
     * P13n dialog save action listeners
     */
    // this.isOrgUserPatched$.pipe(filter((isOrgUserPatched: boolean) => isOrgUserPatched))
    //   .subscribe((isOrgUserPatched: boolean) => {
    //     if (confirm('Your webpage will refresh to apply this change.\nDo you want to refresh now?')) {
    //       this.p13nDialog.componentInstance.isLoading = false;
    //       this.p13nDialog.close();
    //       window.location.reload();
    //     }
    //   });
    // this.isOrgUserPatchError$.pipe(filter((isOrgUserPatchError: boolean) => isOrgUserPatchError))
    //   .subscribe((isOrgUserPatchError: boolean) => {
    //     alert('Failed to change settings.\nPlease try again later');
    //     this.p13nDialog.componentInstance.isLoading = false;
    //     this.p13nDialog.close();
    //   });

    // Watch route changes
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        withLatestFrom(this.accessToken$, this.user$)
      )
      .subscribe(([event, accessToken, user]: [RouterEvent, string, User]) => {
        if (event instanceof NavigationEnd) {
          this.isInitialized = true;

          // Extract only the root route from the current url
          this.rootRoute = this.router.url.match(/^(\/\w*)/)[0];
        }
      });

    this.actions$
      .pipe(
        ofType(coreUser.SET_ORG_USER_ACCESS_SUCCESS),
        takeUntil(this.destroyed$),
        tap(() => {
          this.accessMgmtDialog.componentInstance.isLoading = false;
          this.accessMgmtDialog.close();
        })
      )
      .subscribe();
    this.actions$
      .pipe(
        ofType(coreUser.SET_ORG_USER_ACCESS_FAIL),
        takeUntil(this.destroyed$),
        tap(() => {
          this.accessMgmtDialog.componentInstance.isLoading = false;
          alert('Failed to apply changes.\nPlease try again later.');
        })
      )
      .subscribe();

    combineLatest(
      this.selfOrganizationUser$,
      this.selectedOrganization$,
      (
        selfOrganizationUser: SelfOrganizationUser,
        selectedOrganization: OrganizationWithToken
      ) => {
        if (
          selfOrganizationUser.role === OrganizationUserRole.Admin ||
          selfOrganizationUser.role === OrganizationUserRole.Owner
        ) {
          this.authService
            .requestDeveloperToken(selectedOrganization.token)
            .subscribe((devToken: string) => {
              this.developerToken = devToken;
            });
        }
      }
    ).subscribe();

    // Device Name Editor
    this.isPostDeviceNamesLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPostDeviceNamesLoading: boolean) => {
        if (this.deviceNameEditorDialog) {
          this.deviceNameEditorDialog.componentInstance.isLoading = isPostDeviceNamesLoading;
        }
      });
    this.isPostDeviceNamesLoaded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPostDeviceNamesLoaded: boolean) => {
        this.deviceNameEditorDialog.close();
      });
    this.postDeviceNamesError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((postDeviceNamesError: string) => {
        alert(
          `Failed to change device names.\nPlease try again later.\n${postDeviceNamesError}`
        );
      });

    // LED Commands
    this.isPostShowDevicesLedLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPostShowDevicesLedLoading: boolean) => {
        if (this.deviceSettingsDialog) {
          this.deviceSettingsDialog.componentInstance.isLoading = isPostShowDevicesLedLoading;
        }
      });
    this.isPostShowDevicesLedLoaded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPostShowDevicesLedLoaded: boolean) => {
        if (this.deviceSettingsDialog) {
          this.deviceSettingsDialog.componentInstance.requestSuccess();
        }
      });
    this.postShowDevicesLedError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((postShowDevicesLedError: string) => {
        if (this.deviceSettingsDialog) {
          this.deviceSettingsDialog.componentInstance.requestError();
        }
      });
    this.isPostHideDevicesLedLoading$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPostHideDevicesLedLoading: boolean) => {
        if (this.deviceSettingsDialog) {
          this.deviceSettingsDialog.componentInstance.isLoading = isPostHideDevicesLedLoading;
        }
      });
    this.isPostHideDevicesLedLoaded$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPostHideDevicesLedLoaded: boolean) => {
        if (this.deviceSettingsDialog) {
          this.deviceSettingsDialog.componentInstance.requestSuccess();
        }
      });
    this.postHideDevicesLedError$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((postHideDevicesLedError: string) => {
        if (this.deviceSettingsDialog) {
          this.deviceSettingsDialog.componentInstance.requestError();
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  openP13n(organizationToken: string, temperatureUnit: TemperatureUnit): void {
    const dialogRef = this.dialog.open(P13nDialogComponent, {
      disableClose: true
    });
    dialogRef.componentInstance.organizationToken = organizationToken;
    dialogRef.componentInstance.temperatureUnit = temperatureUnit;
    dialogRef.afterClosed().subscribe((data: { isSettingChanged: boolean }) => {
      if (data.isSettingChanged) {
        if (
          confirm(
            'Your webpage will refresh to apply this change.\nDo you want to refresh now?'
          )
        ) {
          window.location.reload();
        }
      }
    });
  }

  openOrgList(): void {
    this.dialog.open(OrganizationListDialogContainer, { disableClose: true });
  }

  onDropdownEvent(event: MouseEvent, dropdownContainer: HTMLElement): void {
    const dropdownEl: HTMLElement = dropdownContainer.querySelector('ul');

    if (
      !dropdownContainer.contains(event.target as HTMLElement) &&
      dropdownEl
    ) {
      // If selected element is not dropdown element,
      dropdownEl.style.display = 'none';
    } else if (dropdownEl) {
      // If selected element is dropdown element, make it toggle
      // On init, element's display is set to ''

      if (
        dropdownEl.style.display === 'none' ||
        dropdownEl.style.display === ''
      ) {
        dropdownEl.style.display = 'block';
        // this.googleAnalyticsService.emitEvent('Settings', 'open');
      } else {
        dropdownEl.style.display = 'none';
      }
    }
  }

  openDeviceSettings(): void {
    this.deviceSettingsDialog = this.dialog.open(DeviceSettingDialogComponent, {
      disableClose: true
    });
    this.deviceSettingsDialog.componentInstance.openDeviceNameEditor
      .pipe(withLatestFrom(this.devices$))
      .subscribe(([res, devices]: [any, DeviceWithUuid[]]) => {
        this.deviceNameEditorDialog = this.dialog.open(
          DeviceNameEditorDialogComponent,
          { disableClose: true }
        );
        this.deviceNameEditorDialog.componentInstance.devices = devices;
        this.deviceNameEditorDialog.componentInstance.save
          .pipe(withLatestFrom(this.selectedOrganization$))
          .subscribe(
            ([deviceNameSettings, organizationWithToken]: [
              DeviceNameSetting[],
              OrganizationWithToken
            ]) => {
              this.afbApiStore.dispatch(
                new PostDeviceNames({
                  organizationToken: organizationWithToken.token,
                  organizationId: organizationWithToken.id,
                  deviceSettings: deviceNameSettings
                })
              );
            }
          );
      });
    this.deviceSettingsDialog.componentInstance.showAllLed
      .pipe(withLatestFrom(this.devices$, this.selectedOrganization$))
      .subscribe(
        ([res, devices, organizationWithToken]: [
          any,
          DeviceWithUuid[],
          OrganizationWithToken
        ]) => {
          this.afbApiStore.dispatch(
            new PostShowDevicesLed({
              organizationToken: organizationWithToken.token,
              organizationId: organizationWithToken.id,
              deviceUuids: devices.map((device: DeviceWithUuid) => device.uuid)
            })
          );
        }
      );
    this.deviceSettingsDialog.componentInstance.hideAllLed
      .pipe(withLatestFrom(this.devices$, this.selectedOrganization$))
      .subscribe(
        ([res, devices, organizationWithToken]: [
          any,
          DeviceWithUuid[],
          OrganizationWithToken
        ]) => {
          this.afbApiStore.dispatch(
            new PostHideDevicesLed({
              organizationToken: organizationWithToken.token,
              organizationId: organizationWithToken.id,
              deviceUuids: devices.map((device: DeviceWithUuid) => device.uuid)
            })
          );
        }
      );
  }

  openAccessMgmt(organizationToken: string): void {
    // Update organization user list on opening access mgmt
    this.rootStore.dispatch(
      new coreUser.RequestOrgUserListAction(organizationToken)
    );

    this.afbApiStore.dispatch(new GetOrganizationUsers({ organizationToken }));

    this.accessMgmtDialog = this.dialog.open(AccessMgmtDialogComponent, {
      disableClose: true
    });
    this.googleAnalyticsService.emitEvent('Settings', 'open', 'Members');

    this.accessMgmtDialog.componentInstance.reinviteMember.subscribe(
      ({ email, role, pendingGroups, ...rest }: OrganizationUser) => {
        const groupIds: number[] = pendingGroups.map(
          (groupId: number) => groupId
        );
        if (groupIds.length > 0) {
          if (confirm(`Invitation email will be resent to ${email}`)) {
            groupIds.forEach((groupId: number) => {
              this.afbApiStore.dispatch(
                new PostGroupInvitation({
                  email,
                  role,
                  organizationToken,
                  groupId: groupId.toString()
                })
              );
            });
          }
        } else {
          alert('There is no pending group for this member');
        }
      }
    );
    this.accessMgmtDialog.componentInstance.reinviteAdmin.subscribe(
      ({ email, role, ...rest }: OrganizationUser) => {
        if (confirm(`Invitation email will be resent to ${email}`)) {
          this.afbApiStore.dispatch(
            new PostOrganizationInvitation({ email, role, organizationToken })
          );
        }
      }
    );
    this.accessMgmtDialog.componentInstance.save.subscribe(
      (membersData: {
        inviteeList: string[];
        removeList: OrganizationUser[];
        removePendingAdminList: OrganizationUser[];
        removePendingMemberList: OrganizationUser[];
        removeAllGroupList: OrganizationUser[];
      }) => {
        this.accessMgmtDialog.componentInstance.isLoading = true;
        this.rootStore.dispatch(
          new coreUser.SetOrgUserAccessAction({
            orgToken: organizationToken,
            inviteAdminList: membersData.inviteeList,
            removeUserList: membersData.removeList,
            removePendingAdminList: membersData.removePendingAdminList,
            removePendingMemberList: membersData.removePendingMemberList,
            removeAllGroupList: membersData.removeAllGroupList
          })
        );
      }
    );
  }

  openGroups(): void {
    const groupDialogRef = this.dialog.open(GroupComponent, {
      disableClose: true
    });
    this.googleAnalyticsService.emitEvent('Settings', 'open', 'Groups');
    groupDialogRef.afterClosed().subscribe();
  }

  copyDevToken(organizationToken: string): void {
    if (this.developerToken != null && this.developerToken !== '') {
      // If developer token has been cached, just copy to clipboard
      this.clipboardService.copy(this.developerToken).subscribe(
        (devToken: string) => {
          alert('Developer Token has been copied to the Clipboard.');
        },
        (error: Response) => {
          alert('Failed to copy Developer Token.\nPlease try again later.');
        }
      );
    } else {
      // Request for developer token if it hasn't been cached
      this.authService
        .requestDeveloperToken(organizationToken)
        .pipe(
          mergeMap((devToken: string) => this.clipboardService.copy(devToken))
        )
        .subscribe(
          (resDevToken: string) => {
            this.developerToken = resDevToken;
            alert('Developer Token has been copied to the Clipboard.');
          },
          (error: HttpErrorResponse) => {
            alert('Failed to copy Developer Token.\nPlease try again later.');
          }
        );
    }
  }

  logIn(): void {
    this.router.navigate(['/login']);
  }

  signUp(): void {
    this.router.navigate(['/signup']);
  }

  logOut(): void {
    if (confirm('Are you sure you want to log out?')) {
      this.afbGraphqlApiStore.dispatch(new Logout());
    }
  }

  goHome(): void {
    this.rootStore.dispatch(new ClearState());
    this.router.navigate(['/']);
  }
}
